import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { leadsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { timeAgo, formatDateWithTimezone } from '../utils/dateUtils';

const LeadDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isDark } = useTheme();

    const [lead, setLead] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [notes, setNotes] = useState('');
    const [leadNotes, setLeadNotes] = useState([]);
    const [addingNote, setAddingNote] = useState(false);
    const [copied, setCopied] = useState(null);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [statusNotification, setStatusNotification] = useState(null);

    // Fetch lead data
    const fetchLead = useCallback(async () => {
        try {
            setLoading(true);
            const response = await leadsAPI.getLeadById(id);
            const data = response.data;
            const leadData = data.lead || data;
            setLead(leadData);

            // Build conversation: lead's inquiry first, then API messages
            const apiMessages = data.messages || [];
            const conversation = [];

            // Add lead's initial inquiry as the first inbound message
            if (leadData.lead_message) {
                conversation.push({
                    id: 'inquiry',
                    direction: 'inbound',
                    message_content: leadData.lead_message,
                    sent_at: leadData.received_at || leadData.created_at,
                    channel_type: 'email',
                });
            }

            // Add the rest from the API
            conversation.push(...apiMessages);
            setMessages(conversation);

            setError(null);
        } catch (err) {
            console.error('Error fetching lead:', err);
            setError('Lead not found');
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchNotes = useCallback(async () => {
        try {
            const res = await leadsAPI.getNotes(id);
            setLeadNotes(res.data.notes || []);
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchLead();
            fetchNotes();
        }
    }, [id, fetchLead, fetchNotes]);

    const handleAddNote = async (presetNote = null) => {
        const noteContent = presetNote || notes;
        if (!noteContent.trim()) return;

        try {
            setAddingNote(true);
            const res = await leadsAPI.addNote(id, noteContent);
            setLeadNotes([res.data.note, ...leadNotes]);
            if (!presetNote) setNotes('');
            setStatusNotification('Note added successfully!');
            setTimeout(() => setStatusNotification(null), 2000);
        } catch (err) {
            console.error('Error adding note:', err);
            setStatusNotification('Failed to add note');
            setTimeout(() => setStatusNotification(null), 3000);
        } finally {
            setAddingNote(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await leadsAPI.deleteNote(noteId);
            setLeadNotes(leadNotes.filter(n => n.id !== noteId));
            setStatusNotification('Note deleted!');
            setTimeout(() => setStatusNotification(null), 2000);
        } catch (err) {
            console.error('Error deleting note:', err);
            setStatusNotification('Failed to delete note');
            setTimeout(() => setStatusNotification(null), 3000);
        }
    };

    // Helpers


    const formatResponseTime = (seconds) => {
        if (seconds == null) return '-';
        if (seconds < 60) return `${seconds}s`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
        return `${Math.floor(seconds / 3600)}h`;
    };

    const getStatusStyle = (status) => {
        const s = (status || '').toLowerCase();
        const styles = {
            new: { bg: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-500' },
            contacted: { bg: 'bg-blue-500/10 border-blue-500/20', text: 'text-blue-400' },
            qualified: { bg: 'bg-green-500/10 border-green-500/20', text: 'text-green-400' },
            unqualified: { bg: 'bg-red-500/10 border-red-500/20', text: 'text-red-400' },
            'closed won': { bg: 'bg-emerald-500/10 border-emerald-500/20', text: 'text-emerald-400' },
            'closed lost': { bg: 'bg-gray-500/10 border-gray-500/20', text: 'text-gray-400' },
        };
        return styles[s] || styles['new'];
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getEffectiveLastContact = () => {
        if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            return lastMsg.sent_at || lastMsg.created_at;
        }
        return lead?.responded_at || lead?.received_at || lead?.created_at;
    };

    // Helper function moved to backend, we now use lead.lead_score.
    const getScoreBadgeStyle = (tier) => {
        switch (tier) {
            case 'hot': return 'bg-red-500/10 text-red-400 border-red-500/30';
            case 'warm': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
            case 'cold': return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
            default: return 'bg-white/5 text-gray-400 border-white/10';
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopied(field);
        setTimeout(() => setCopied(null), 2000);
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await leadsAPI.deleteLead(id);
            navigate('/leads');
        } catch (err) {
            console.error('Error deleting lead:', err);
            alert('Failed to delete lead.');
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            setStatusUpdating(true);
            const response = await leadsAPI.updateLeadStatus(id, newStatus);
            setLead(prev => ({ ...prev, ...response.data }));
            setStatusNotification('Status updated!');
            setTimeout(() => setStatusNotification(null), 2000);
        } catch (err) {
            console.error('Error updating status:', err);
            setStatusNotification('Failed to update status');
            setTimeout(() => setStatusNotification(null), 3000);
        } finally {
            setStatusUpdating(false);
        }
    };

    const getSourceIcon = (platform) => {
        const p = (platform || '').toLowerCase();
        if (p.includes('zillow')) return 'home';
        if (p.includes('redfin')) return 'search';
        if (p.includes('realtor')) return 'apartment';
        if (p.includes('rightmove')) return 'explore';
        if (p.includes('99acres')) return 'landscape';
        return 'language';
    };

    const formatChannels = (channels) => {
        if (!channels) return 'N/A';
        return channels.split(',')
            .map(c => c.trim())
            .map(c => {
                const lower = c.toLowerCase();
                if (lower === 'sms') return 'SMS';
                if (lower === 'whatsapp') return 'WhatsApp';
                return c.charAt(0).toUpperCase() + c.slice(1);
            })
            .join(' & ');
    };

    // Strip HTML tags from email template content and clean up whitespace
    const stripHtml = (html) => {
        if (!html) return '';

        // Helper: collapse multiple blank lines into single newline
        const cleanWhitespace = (text) => {
            return text
                .replace(/\r\n/g, '\n')       // Normalize Windows line endings
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0)
                .join('\n');
        };

        // If it's not HTML at all, just clean up whitespace
        if (!html.includes('<')) return cleanWhitespace(html);

        // Remove <style>, <script>, and <head> blocks entirely
        let cleaned = html
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');

        // Create a temporary DOM element
        const tmp = document.createElement('div');
        tmp.innerHTML = cleaned;

        // If this is a full branded email template, extract ONLY the .content area
        const contentArea = tmp.querySelector('.content') || tmp.querySelector('td.content');
        if (contentArea) {
            // Remove non-message elements inside .content
            contentArea.querySelectorAll('.text-sm').forEach(el => {
                const t = el.textContent || '';
                if (t.includes('receiving this') || t.includes('Manage in')) el.remove();
            });
            contentArea.querySelectorAll('.btn').forEach(el => el.remove());
            contentArea.querySelectorAll('.divider').forEach(el => el.remove());

            return cleanWhitespace(contentArea.textContent || contentArea.innerText || '');
        }

        // Fallback: remove branding wrappers if present
        ['.logo-container', '.footer', '[style*="max-height:0"]'].forEach(sel => {
            tmp.querySelectorAll(sel).forEach(el => el.remove());
        });

        // Replace structural tags with newlines for better formatting
        let result = tmp.innerHTML
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n')
            .replace(/<\/div>/gi, '\n')
            .replace(/<\/tr>/gi, '\n')
            .replace(/<\/li>/gi, '\n');

        const tmp2 = document.createElement('div');
        tmp2.innerHTML = result;

        return cleanWhitespace(tmp2.textContent || tmp2.innerText || '');
    };

    // Loading skeleton
    if (loading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className={`h-4 w-48 rounded ${isDark ? 'bg-white/5' : 'bg-teal/10'}`}></div>
                <div className="flex items-center gap-6">
                    <div className={`w-16 h-16 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-teal/10'}`}></div>
                    <div className="space-y-2">
                        <div className={`h-8 w-56 rounded ${isDark ? 'bg-white/5' : 'bg-teal/10'}`}></div>
                        <div className={`h-4 w-32 rounded ${isDark ? 'bg-white/5' : 'bg-teal/10'}`}></div>
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        <div className={`h-64 rounded-xl ${isDark ? 'bg-dark-800' : 'bg-white'}`}></div>
                        <div className={`h-48 rounded-xl ${isDark ? 'bg-dark-800' : 'bg-white'}`}></div>
                    </div>
                    <div className="space-y-6">
                        <div className={`h-40 rounded-xl ${isDark ? 'bg-dark-800' : 'bg-white'}`}></div>
                        <div className={`h-32 rounded-xl ${isDark ? 'bg-dark-800' : 'bg-white'}`}></div>
                    </div>
                </div>
            </div>
        );
    }

    // Error / Not found
    if (error || !lead) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${isDark ? 'bg-dark-800' : 'bg-mint'}`}>
                    <span className={`material-symbols-outlined text-4xl ${isDark ? 'text-light/20' : 'text-darkTeal/20'}`}>person_off</span>
                </div>
                <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>Lead not found</h2>
                <p className={`text-sm mb-6 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>This lead may have been deleted or doesn't exist.</p>
                <button
                    onClick={() => navigate('/leads')}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${isDark ? 'bg-cyan/10 border border-cyan/30 text-cyan hover:bg-cyan/20' : 'bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20'}`}
                >
                    <span className="material-symbols-outlined text-lg">arrow_back</span> Back to Leads
                </button>
            </div>
        );
    }

    const statusStyle = getStatusStyle(lead.status);

    return (
        <div>
            {/* Breadcrumb */}
            <nav className="mb-6">
                <ol className={`flex items-center gap-2 text-sm font-medium ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>
                    <li>
                        <Link to="/leads" className={`hover:${isDark ? 'text-cyan' : 'text-primary'} cursor-pointer transition-colors`}>Leads</Link>
                    </li>
                    <li><span className="material-symbols-outlined text-sm">chevron_right</span></li>
                    <li className={isDark ? 'text-light/80' : 'text-darkTeal/80'}>{lead.lead_name || 'Unknown Lead'}</li>
                </ol>
            </nav>

            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
                <div className="flex items-center gap-6">
                    <div className="relative">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black border-2 ${isDark ? 'bg-cyan/10 border-cyan/30 text-cyan' : 'bg-primary/10 border-primary/30 text-primary'}`}>
                            {getInitials(lead.lead_name)}
                        </div>
                        <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-navy rounded-full"></span>
                    </div>
                    <div>
                        <div className="flex items-center gap-3">
                            <h2 className={`text-2xl md:text-3xl font-bold tracking-tight ${isDark ? 'text-light' : 'text-darkTeal'}`}>{lead.lead_name || 'Unknown Lead'}</h2>
                            <span className={`px-3 py-1 border rounded-full text-[10px] font-black tracking-widest uppercase ${statusStyle.bg} ${statusStyle.text}`}>
                                {lead.status || 'New'}
                            </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                            <p className={`text-sm font-medium flex items-center gap-1.5 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                                <span className="material-symbols-outlined text-sm">{getSourceIcon(lead.source_platform)}</span>
                                From {lead.source_platform || 'Unknown'}
                            </p>
                            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/10' : 'bg-darkTeal/10'}`}></span>
                            {getEffectiveLastContact() && (
                                <p className="text-[11px] font-bold text-green-400 uppercase tracking-wider">
                                    Last contact: {timeAgo(getEffectiveLastContact())}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-3">
                    {lead.lead_phone && (
                        <a href={`tel:${lead.lead_phone}`} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border text-xs font-bold uppercase tracking-widest transition-all ${isDark ? 'border-cyan/40 text-cyan hover:bg-cyan/10' : 'border-primary/40 text-primary hover:bg-primary/10'}`}>
                            <span className="material-symbols-outlined text-lg">call</span> Call
                        </a>
                    )}
                    {lead.lead_phone && (
                        <a href={`https://wa.me/${lead.lead_phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-green-500/40 text-green-400 hover:bg-green-500/10 text-xs font-bold uppercase tracking-widest transition-all">
                            <span className="material-symbols-outlined text-lg">chat</span> WhatsApp
                        </a>
                    )}
                    {lead.lead_email && (
                        <a href={`mailto:${lead.lead_email}`} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border text-xs font-bold uppercase tracking-widest transition-all ${isDark ? 'border-white/20 text-light hover:bg-white/5' : 'border-darkTeal/20 text-darkTeal hover:bg-darkTeal/5'}`}>
                            <span className="material-symbols-outlined text-lg">mail</span> Email
                        </a>
                    )}
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-bold uppercase tracking-widest transition-all"
                    >
                        <span className="material-symbols-outlined text-lg">delete</span> Delete
                    </button>
                </div>
            </div>

            {/* Main Content: 2-Column Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* LEFT COLUMN */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Contact Information */}
                    <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800 border-white/5' : 'bg-white border-teal/20 shadow-lg'}`}>
                        <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-white/5 bg-dark-900/30' : 'border-teal/10 bg-mint/30'}`}>
                            <h3 className={`text-sm font-bold uppercase tracking-widest ${isDark ? 'text-light' : 'text-darkTeal'}`}>Contact Information</h3>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* Email */}
                                <div className={`p-4 rounded-xl text-center relative ${isDark ? 'bg-dark-900/40' : 'bg-mint/30'}`}>
                                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Email Address</p>
                                    <p className={`text-sm font-semibold break-all ${isDark ? 'text-light' : 'text-darkTeal'}`}>{lead.lead_email || 'N/A'}</p>
                                    {lead.lead_email && (
                                        <button onClick={() => copyToClipboard(lead.lead_email, 'email')} className={`material-symbols-outlined text-base absolute top-3 right-3 transition-colors ${copied === 'email' ? (isDark ? 'text-cyan' : 'text-primary') : (isDark ? 'text-light/20 hover:text-cyan' : 'text-darkTeal/20 hover:text-primary')}`}>
                                            {copied === 'email' ? 'check' : 'content_copy'}
                                        </button>
                                    )}
                                </div>
                                {/* Property Interest */}
                                <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-dark-900/40' : 'bg-mint/30'}`}>
                                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Property Interest</p>
                                    <p className={`text-sm font-bold ${isDark ? 'text-cyan' : 'text-primary'}`}>{lead.property_address || 'N/A'}</p>
                                </div>
                                {/* Phone */}
                                <div className={`p-4 rounded-xl text-center relative ${isDark ? 'bg-dark-900/40' : 'bg-mint/30'}`}>
                                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Phone Number</p>
                                    <div className="flex items-center justify-center gap-2">
                                        <span className={`text-sm font-semibold ${isDark ? 'text-light' : 'text-darkTeal'}`}>{lead.lead_phone || 'N/A'}</span>
                                        {lead.lead_country && <span className="text-sm">{lead.lead_country === 'US' ? '🇺🇸' : lead.lead_country === 'IN' ? '🇮🇳' : lead.lead_country === 'GB' ? '🇬🇧' : '🌍'}</span>}
                                    </div>
                                    {lead.lead_phone && (
                                        <button onClick={() => copyToClipboard(lead.lead_phone, 'phone')} className={`material-symbols-outlined text-base absolute top-3 right-3 transition-colors ${copied === 'phone' ? (isDark ? 'text-cyan' : 'text-primary') : (isDark ? 'text-light/20 hover:text-cyan' : 'text-darkTeal/20 hover:text-primary')}`}>
                                            {copied === 'phone' ? 'check' : 'content_copy'}
                                        </button>
                                    )}
                                </div>
                                {/* Property Type */}
                                <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-dark-900/40' : 'bg-mint/30'}`}>
                                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Type</p>
                                    <p className={`text-sm font-semibold ${isDark ? 'text-light' : 'text-darkTeal'}`}>{lead.property_type || 'N/A'}</p>
                                </div>
                                {/* Country */}
                                <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-dark-900/40' : 'bg-mint/30'}`}>
                                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Country</p>
                                    <p className={`text-sm font-semibold ${isDark ? 'text-light' : 'text-darkTeal'}`}>{lead.lead_country || 'N/A'}</p>
                                </div>
                                {/* Contacted Via */}
                                <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-dark-900/40' : 'bg-mint/30'}`}>
                                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>{lead.contacted_via ? 'Contacted Via' : 'Received'}</p>
                                    <p className={`text-sm font-semibold ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                                        {lead.contacted_via ? formatChannels(lead.contacted_via) : (lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lead Message / Initial Inquiry */}
                    {lead.lead_message && (
                        <div className={`rounded-xl border p-6 relative overflow-hidden group ${isDark ? 'bg-dark-900 border-cyan/30' : 'bg-primary/5 border-primary/20'}`}>
                            <div className="absolute top-0 right-0 p-4">
                                <span className={`material-symbols-outlined text-4xl ${isDark ? 'text-cyan/20' : 'text-primary/20'}`}>format_quote</span>
                            </div>
                            <p className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${isDark ? 'text-cyan' : 'text-primary'}`}>Initial Inquiry Message</p>
                            <p className={`text-sm leading-normal italic whitespace-pre-wrap ${isDark ? 'text-light/80' : 'text-darkTeal/80'}`}>
                                "{stripHtml(lead.lead_message).split('[Additional Inquiry')[0].trim()}"
                            </p>
                            <div className={`mt-4 pt-4 border-t flex items-center justify-between ${isDark ? 'border-white/5' : 'border-darkTeal/10'}`}>
                                <span className={`text-[10px] ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>Received via {lead.source_platform || 'Unknown'}</span>
                                <span className={`text-[10px] ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>
                                    {lead.created_at ? formatDateWithTimezone(lead.created_at, user?.timezone, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                    {lead.received_at ? ` • ${formatDateWithTimezone(lead.received_at, user?.timezone, { hour: '2-digit', minute: '2-digit' })}` : ''}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Activity Timeline */}
                    <div className={`relative pl-8 space-y-8 before:content-[''] before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] ${isDark ? 'before:bg-white/10' : 'before:bg-teal/10'}`}>
                        <div className="relative">
                            <div className={`absolute -left-[28px] top-1.5 w-[14px] h-[14px] rounded-full border-2 shadow-[0_0_8px_rgba(8,217,214,0.5)] ${isDark ? 'border-navy bg-cyan' : 'border-white bg-primary'}`}></div>
                            <div>
                                <p className={`text-xs font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Lead Received</p>
                                <p className={`text-[11px] ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>
                                    {lead.source_platform || 'Unknown source'} • {lead.created_at ? formatDateWithTimezone(lead.created_at, user?.timezone, { hour: '2-digit', minute: '2-digit' }) : ''}
                                </p>
                            </div>
                        </div>
                        {lead.responded_at && (
                            <div className="relative">
                                <div className={`absolute -left-[28px] top-1.5 w-[14px] h-[14px] rounded-full border-2 ${isDark ? 'border-navy bg-cyan' : 'border-white bg-primary'}`}></div>
                                <div>
                                    <p className={`text-xs font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Auto-response sent</p>
                                    <p className={`text-[11px] ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>
                                        via {lead.contacted_via ? formatChannels(lead.contacted_via) : 'email'} • {formatDateWithTimezone(lead.responded_at, user?.timezone, { hour: '2-digit', minute: '2-digit' })}
                                        {lead.response_time_seconds != null && ` • Response in ${formatResponseTime(lead.response_time_seconds)}`}
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="relative">
                            <div className={`absolute -left-[28px] top-1.5 w-[14px] h-[14px] rounded-full border-2 ${isDark ? 'border-navy bg-white/20' : 'border-white bg-darkTeal/20'}`}></div>
                            <div>
                                <p className={`text-xs font-bold ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>Awaiting reply</p>
                                <p className={`text-[11px] ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Current status</p>
                            </div>
                        </div>
                    </div>

                    {/* Conversation History */}
                    <div className={`rounded-xl border flex flex-col ${isDark ? 'bg-dark-800 border-white/5' : 'bg-white border-teal/20 shadow-lg'}`} style={{ minHeight: '400px', maxHeight: '600px' }}>
                        <div className={`p-4 border-b flex items-center justify-between ${isDark ? 'border-white/5' : 'border-teal/10'}`}>
                            <div className="flex items-center gap-3">
                                <span className={`material-symbols-outlined ${isDark ? 'text-cyan' : 'text-primary'}`}>forum</span>
                                <h3 className={`text-sm font-bold uppercase tracking-widest ${isDark ? 'text-light' : 'text-darkTeal'}`}>Conversation</h3>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-4" style={{ scrollbarWidth: 'none' }}>
                            {messages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <span className={`material-symbols-outlined text-4xl mb-3 ${isDark ? 'text-light/20' : 'text-darkTeal/20'}`}>chat_bubble_outline</span>
                                    <p className={`text-sm font-medium ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>No conversation yet</p>
                                    <p className={`text-xs ${isDark ? 'text-light/20' : 'text-darkTeal/20'}`}>Messages will appear here once sent</p>
                                </div>
                            ) : (
                                messages.map((msg, idx) => (
                                    msg.direction === 'outbound' ? (
                                        /* Outbound message */
                                        <div key={msg.id || idx} className="flex items-start gap-3 max-w-[85%] ml-auto flex-row-reverse">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-cyan/10 border border-cyan/20 text-cyan' : 'bg-primary/10 border border-primary/20 text-primary'}`}>
                                                <span className="material-symbols-outlined text-sm">smart_toy</span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                <div className={`p-4 rounded-2xl rounded-tr-none text-sm leading-normal whitespace-pre-wrap text-left ${isDark ? 'bg-cyan/10 border border-cyan/30 text-light' : 'bg-primary/10 border border-primary/30 text-darkTeal'}`}>
                                                    {stripHtml(msg.message_content || msg.content)}
                                                    {(msg.delivery_status === 'sent' || msg.isAutoReply) && (
                                                        <div className="mt-2 flex justify-end">
                                                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border uppercase tracking-tighter ${isDark ? 'bg-cyan/20 text-cyan border-cyan/30' : 'bg-primary/20 text-primary border-primary/30'}`}>Auto-reply</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className={`text-[10px] mt-2 mr-1 ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>
                                                    {msg.sent_at || msg.created_at ? formatDateWithTimezone(msg.sent_at || msg.created_at, user?.timezone, { hour: '2-digit', minute: '2-digit' }) : ''}
                                                    {msg.delivery_status && ` • ${msg.delivery_status}`}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Inbound message */
                                        <div key={msg.id || idx} className="flex items-start gap-3 max-w-[85%]">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${isDark ? 'bg-white/10 text-light' : 'bg-teal/10 text-darkTeal'}`}>
                                                {getInitials(lead.lead_name)}
                                            </div>
                                            <div>
                                                <div className={`p-4 rounded-2xl rounded-tl-none text-sm leading-normal whitespace-pre-wrap ${isDark ? 'bg-dark-900 border border-white/5 text-light/90' : 'bg-mint border border-teal/10 text-darkTeal/90'}`}>
                                                    {stripHtml(msg.message_content || msg.content)}
                                                </div>
                                                <p className={`text-[10px] mt-2 ml-1 ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>
                                                    {msg.sent_at || msg.created_at ? formatDateWithTimezone(msg.sent_at || msg.created_at, user?.timezone, { hour: '2-digit', minute: '2-digit' }) : ''}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                ))
                            )}
                        </div>

                        {/* Message input */}
                        <div className={`p-4 border-t ${isDark ? 'bg-dark-900/50 border-white/5' : 'bg-mint/30 border-teal/10'}`}>
                            <div className="relative">
                                <textarea
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    className={`w-full rounded-xl p-3 text-sm resize-none pr-16 transition-all focus:outline-none focus:ring-1 ${isDark ? 'bg-navy border border-white/10 text-light placeholder-light/20 focus:ring-cyan focus:border-cyan' : 'bg-white border border-teal/20 text-darkTeal placeholder-darkTeal/30 focus:ring-primary focus:border-primary'}`}
                                    placeholder="Type a message..."
                                    rows="2"
                                />
                                <button className={`absolute right-2 bottom-2 p-2 rounded-lg hover:scale-105 transition-transform ${isDark ? 'bg-cyan text-navy' : 'bg-primary text-white'}`}>
                                    <span className="material-symbols-outlined">send</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN - Sidebar */}
                <div className="space-y-6">

                    {/* Engagement Overview */}
                    <div className={`rounded-xl border p-6 ${isDark ? 'bg-dark-800 border-white/5' : 'bg-white border-teal/20 shadow-lg'}`}>
                        <h3 className={`text-xs font-bold uppercase tracking-[0.15em] mb-6 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Engagement Overview</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-900/50 border-white/5' : 'bg-mint/50 border-teal/10'}`}>
                                <p className={`text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Response Time</p>
                                <div className={`flex items-center gap-1.5 font-black text-lg ${isDark ? 'text-cyan' : 'text-primary'}`}>
                                    <span className="material-symbols-outlined text-lg">bolt</span>
                                    <span>{formatResponseTime(lead.response_time_seconds)}</span>
                                </div>
                            </div>
                            <div className={`p-4 rounded-xl border ${isDark ? 'bg-dark-900/50 border-white/5' : 'bg-mint/50 border-teal/10'}`}>
                                <p className={`text-[10px] font-bold uppercase mb-1 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Messages</p>
                                <p className={`text-lg font-black ${isDark ? 'text-light' : 'text-darkTeal'}`}>{messages.length}</p>
                            </div>
                        </div>
                        {lead.lead_score && (
                            <div className={`mt-4 p-4 rounded-xl flex items-center justify-between border ${getScoreBadgeStyle(lead.lead_score.tier)}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-xl">{lead.lead_score.icon}</span>
                                    <p className="text-xs font-bold uppercase tracking-widest leading-none">Lead Score</p>
                                </div>
                                <div className="flex flex-col items-end">
                                    <span className="text-sm font-black uppercase">{lead.lead_score.tier}</span>
                                    <span className="text-[10px] opacity-70">({lead.lead_score.score} pts)</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Pipeline Status */}
                    <div className={`rounded-xl border p-6 ${isDark ? 'bg-dark-800 border-white/5' : 'bg-white border-teal/20 shadow-lg'}`}>
                        <h3 className={`text-xs font-bold uppercase tracking-[0.15em] mb-4 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Pipeline Status</h3>
                        <div className="relative">
                            <select
                                value={lead.status || 'new'}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                disabled={statusUpdating}
                                className={`w-full rounded-lg py-2.5 pl-4 pr-10 text-sm cursor-pointer outline-none appearance-none font-bold transition-all focus:ring-1 ${isDark ? 'bg-navy border border-white/10 text-light focus:ring-cyan focus:border-cyan' : 'bg-mint border border-teal/20 text-darkTeal focus:ring-primary focus:border-primary'} ${statusUpdating ? 'opacity-50 cursor-wait' : ''}`}
                            >
                                <option value="new" className={isDark ? 'bg-navy' : 'bg-white'}>New Lead</option>
                                <option value="contacted" className={isDark ? 'bg-navy' : 'bg-white'}>Contacted</option>
                                <option value="qualified" className={isDark ? 'bg-navy' : 'bg-white'}>Qualified</option>
                                <option value="unqualified" className={isDark ? 'bg-navy' : 'bg-white'}>Unqualified</option>
                                <option value="closed won" className={isDark ? 'bg-navy' : 'bg-white'}>Closed / Won</option>
                                <option value="closed lost" className={isDark ? 'bg-navy' : 'bg-white'}>Closed / Lost</option>
                            </select>
                            <span className={`material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>expand_more</span>
                        </div>
                        {lead.updated_at && (
                            <p className={`text-[10px] mt-2 ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>Updated {timeAgo(lead.updated_at)}</p>
                        )}
                    </div>

                    {/* Internal Notes */}
                    <div className={`rounded-xl border p-6 ${isDark ? 'bg-dark-800 border-white/5' : 'bg-white border-teal/20 shadow-lg'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className={`text-xs font-bold uppercase tracking-[0.15em] ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Internal Notes</h3>
                        </div>

                        {user?.subscription_plan === 'starter' || user?.subscription_plan === 'free' ? (
                            <div className={`p-6 rounded-xl border flex flex-col items-center justify-center text-center ${isDark ? 'bg-navy border-cyan/20' : 'bg-mint border-primary/20'}`}>
                                <span className={`material-symbols-outlined text-4xl mb-3 ${isDark ? 'text-cyan' : 'text-primary'}`}>lock</span>
                                <h4 className={`text-sm font-bold mb-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>Pro Feature</h4>
                                <p className={`text-xs mb-4 ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>Internal lead notes are only available on Professional and Team plans.</p>
                                <button onClick={() => navigate('/billing')} className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-opacity hover:opacity-90 ${isDark ? 'bg-cyan text-navy' : 'bg-primary text-white'}`}>
                                    Upgrade Plan
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Note Input */}
                                <div className="mb-6">
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        className={`w-full rounded-lg p-3 text-sm resize-none mb-3 transition-all focus:outline-none focus:ring-1 ${isDark ? 'bg-dark-900 border border-white/10 text-light placeholder-light/20 focus:ring-cyan focus:border-cyan' : 'bg-mint border border-teal/20 text-darkTeal placeholder-darkTeal/30 focus:ring-primary focus:border-primary'}`}
                                        placeholder="Add a note about this lead..."
                                        rows="3"
                                        disabled={addingNote}
                                    />

                                    {/* Quick Add Suggestions */}
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {['Called - no answer', 'Scheduled showing', 'Not interested', 'Follow up in 3 days'].map((suggestion) => (
                                            <button
                                                key={suggestion}
                                                onClick={() => handleAddNote(suggestion)}
                                                disabled={addingNote}
                                                className={`px-2 py-1 text-[10px] rounded-full border transition-all ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10 text-light/70' : 'bg-teal/5 border-teal/10 hover:bg-teal/10 text-darkTeal/70'}`}
                                            >
                                                + {suggestion}
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => handleAddNote()}
                                        disabled={addingNote || !notes.trim()}
                                        className={`w-full py-2.5 flex items-center justify-center gap-2 font-black rounded-lg text-xs uppercase tracking-widest transition-opacity ${(!notes.trim() || addingNote) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'} ${isDark ? 'bg-cyan text-navy' : 'bg-primary text-white'}`}
                                    >
                                        {addingNote ? 'Saving...' : 'Save Note'}
                                    </button>
                                </div>

                                {/* Notes List */}
                                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                                    {leadNotes.length === 0 ? (
                                        <p className={`text-xs text-center italic py-4 ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>No notes added yet.</p>
                                    ) : (
                                        leadNotes.map((note) => (
                                            <div key={note.id} className={`p-3 rounded-lg border relative group ${isDark ? 'bg-dark-900/50 border-white/5' : 'bg-mint/50 border-teal/10'}`}>
                                                <p className={`text-sm mb-2 pr-6 leading-relaxed ${isDark ? 'text-light/90' : 'text-darkTeal/90'}`}>{note.note}</p>
                                                <div className={`text-[10px] flex items-center gap-1 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>
                                                    <span className="material-symbols-outlined text-[10px]">schedule</span>
                                                    {formatDateWithTimezone(note.created_at, user?.timezone, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteNote(note.id)}
                                                    className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/10 text-red-500 transition-all"
                                                    title="Delete Note"
                                                >
                                                    <span className="material-symbols-outlined text-sm">delete</span>
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Status notification toast */}
            {statusNotification && (
                <div className={`fixed bottom-8 right-8 px-5 py-3 rounded-xl text-sm font-bold shadow-2xl z-50 animate-[fadeIn_0.2s_ease-out] ${statusNotification.includes('Failed') ? 'bg-red-500 text-white' : (isDark ? 'bg-cyan text-navy' : 'bg-primary text-white')}`}>
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-lg">{statusNotification.includes('Failed') ? 'error' : 'check_circle'}</span>
                        {statusNotification}
                    </div>
                </div>
            )}

            {/* Delete confirmation modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setShowDeleteConfirm(false)}></div>
                    <div className={`relative w-full max-w-md rounded-2xl border p-8 shadow-2xl ${isDark ? 'bg-navy border-white/10' : 'bg-white border-teal/20'}`}>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mb-5">
                                <span className="material-symbols-outlined text-3xl text-red-500">delete_forever</span>
                            </div>
                            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>Delete this lead?</h3>
                            <p className={`text-sm mb-6 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                                <span className="font-bold">{lead.lead_name}</span> will be permanently deleted. This action cannot be undone.
                            </p>
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={deleting}
                                    className={`flex-1 py-3 rounded-xl text-sm font-bold uppercase tracking-widest transition-all ${isDark ? 'bg-white/5 border border-white/10 text-light hover:bg-white/10' : 'bg-mint border border-teal/20 text-darkTeal hover:bg-teal/10'} ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className={`flex-1 py-3 rounded-xl text-sm font-bold uppercase tracking-widest bg-red-500 text-white hover:bg-red-600 transition-all flex items-center justify-center gap-2 ${deleting ? 'opacity-70 cursor-wait' : ''}`}
                                >
                                    {deleting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Deleting...
                                        </>
                                    ) : 'Delete Lead'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadDetail;
